/**
 * Audio Playback
 * Handles audio playback controls, animation loop, and seek functionality
 */

import { audioState, playbackState, playlistState, lyricsState, uiState, exportState, managers } from './state.js';
import { elements } from './dom-cache.js';
import { createFeatureExtractor } from '../lib/audio-features.js';
import { trackPlaybackStarted } from './analytics/event-tracker.js';
import { VisualizerRegistry } from '../visualizer.js';
import { entitlementService } from '../entitlements/entitlement-service.js';
import { showNotification } from '../lib/notification-utils.js';

/**
 * Check if playback is blocked (e.g., during export)
 * This is a centralized guard to prevent any playback/visualization during export.
 * @returns {boolean} True if playback should be blocked
 */
export function isPlaybackBlocked() {
  return exportState.isExporting;
}

/**
 * Get playlist tracks that are included, preserving their original indices.
 * @returns {Array<{originalIndex: number, included: boolean, ...}>}
 */
function getIncludedTracks() {
  return playlistState.playlist
    .map((t, i) => ({ ...t, originalIndex: i }))
    .filter(t => t.included);
}

/**
 * Ensure a visualizer is set before playback/live mode starts.
 * If no visualizer is active, sets the first allowed visualizer.
 * This implements lazy initialization - visualizer is only set when needed.
 */
export async function ensureVisualizerReady() {
  // Skip if already has a visualizer
  if (managers.visualizerManager?.getType()) {
    return;
  }

  // Get list and entitlements
  const vizList = VisualizerRegistry.getListSync();
  if (vizList.length === 0) return;

  const allowedViz = await entitlementService.getAllowedVisualizers();
  const allAllowed = allowedViz.includes('*');

  // Find first allowed visualizer
  const firstAllowed = allAllowed
    ? vizList[0]
    : (vizList.find(v => allowedViz.includes(v.name)) || vizList[0]);

  // Set the visualizer
  await managers.visualizerManager.setType(firstAllowed.name);

  // Build controls UI for the new visualizer
  const { buildControlsUI } = await import('./visualizer-controls.js');
  buildControlsUI();
}

/**
 * Format time as M:SS
 */
export function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format total playlist duration in a human-friendly way.
 * Under 1 hour: "23 min", at or above 1 hour: "1:23" (h:mm).
 * This avoids confusion with formatTime's M:SS format for individual tracks.
 */
export function formatPlaylistDuration(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours === 0) {
    return `${minutes} min`;
  }
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Update play/pause button icon state
 */
function updatePlayPauseButton(isPlaying) {
  if (!elements.playPauseBtn) return;

  const playIcon = elements.playPauseBtn.querySelector('.play-icon');
  const pauseIcon = elements.playPauseBtn.querySelector('.pause-icon');

  if (playIcon && pauseIcon) {
    // Toggle Lucide icons
    playIcon.style.display = isPlaying ? 'none' : '';
    pauseIcon.style.display = isPlaying ? '' : 'none';
  } else {
    // Fallback to text for legacy UI
    elements.playPauseBtn.textContent = isPlaying ? 'Pause' : 'Play';
  }

  elements.playPauseBtn.title = isPlaying ? 'Pause' : 'Play';
}

/**
 * Update track info display in transport
 */
export function updateTrackInfo() {
  if (!elements.trackInfoEl) return;

  const includedTracks = playlistState.playlist.filter(t => t.included);
  const currentIncludedIndex = includedTracks.findIndex(t => t === playlistState.playlist[playlistState.currentTrackIndex]);
  if (includedTracks.length > 0 && currentIncludedIndex >= 0) {
    elements.trackInfoEl.textContent = `Track ${currentIncludedIndex + 1} of ${includedTracks.length} · `;
  } else {
    elements.trackInfoEl.textContent = '';
  }
}

/**
 * Load a specific track from the playlist
 */
export async function loadTrack(index, { seamless = false } = {}) {
  // Block loading during export
  if (isPlaybackBlocked()) return false;
  if (index < 0 || index >= playlistState.playlist.length) return false;
  if (playbackState.isLoadingTrack) return false;

  playbackState.isLoadingTrack = true;
  playbackState.loadSequence++;
  const thisLoadSequence = playbackState.loadSequence;

  try {
    stopPlayback();
    playlistState.currentTrackIndex = index;

    // Reset feature extractor state for new track (clears onset history)
    if (audioState.featureExtractor) {
      audioState.featureExtractor.reset();
    }
    const track = playlistState.playlist[index];

    // Create audio context if needed
    if (!audioState.audioContext) {
      audioState.audioContext = new AudioContext();
    } else if (audioState.audioContext.state === 'suspended') {
      await audioState.audioContext.resume();
    }

    // Check if a newer load was started during await
    if (playbackState.loadSequence !== thisLoadSequence) {
      return false;
    }

    // Decode audio if not already decoded
    if (!track.audioBuffer) {
      let arrayBuffer;
      if (track.isStreamed && track.streamUrl) {
        // Fetch audio from stream URL (e.g., Suno generated songs)
        const response = await fetch(track.streamUrl);
        if (!response.ok) throw new Error(`Failed to fetch stream: ${response.status}`);
        arrayBuffer = await response.arrayBuffer();
      } else if (track.file) {
        // Standard file-based track
        arrayBuffer = await track.file.arrayBuffer();
      } else {
        throw new Error('Track has no audio source (missing file and streamUrl)');
      }
      track.audioBuffer = await audioState.audioContext.decodeAudioData(arrayBuffer);
      track.duration = track.audioBuffer.duration;
    }

    // Check if a newer load was started during await
    if (playbackState.loadSequence !== thisLoadSequence) {
      return false;
    }

    audioState.audioBuffer = track.audioBuffer;

    // Update UI
    if (elements.fileNameEl) elements.fileNameEl.textContent = track.name;
    if (elements.durationEl) elements.durationEl.textContent = formatTime(track.duration);
    if (elements.seekBar) {
      elements.seekBar.max = track.duration;
      elements.seekBar.value = 0;
    }
    if (elements.currentTimeEl) elements.currentTimeEl.textContent = '0:00';
    playbackState.pauseTime = 0;

    // Calculate lyrics scroll params for this track
    // Pass alignedLyrics if available for time-synced highlighting
    if (track.lyrics && track.duration && elements.lyricsOverlayCanvas) {
      lyricsState.currentLyricsScrollParams = managers.lyricsRenderer.calculateScrollParams(
        track.lyrics,
        track.duration,
        elements.lyricsOverlayCanvas.width,
        elements.lyricsOverlayCanvas.height,
        track.alignedLyrics
      );
    } else {
      lyricsState.currentLyricsScrollParams = null;
    }

    // Import updatePlaylistUI dynamically to avoid circular dependency
    const { updatePlaylistUI } = await import('./playlist.js');
    updatePlaylistUI();

    if (!seamless) {
      managers.visualizerManager.clear();
    }

    return true;

  } catch (error) {
    console.error('Error loading track:', error);
    showNotification('Error loading track: ' + error.message, { type: 'error' });
    return false;
  } finally {
    playbackState.isLoadingTrack = false;
  }
}

/**
 * Start audio playback from a given offset
 */
export async function startPlayback(offset = playbackState.pauseTime) {
  // Block playback during export
  if (isPlaybackBlocked()) return;
  if (!audioState.audioBuffer) return;

  // Resume suspended AudioContext (browser may suspend after inactivity)
  if (audioState.audioContext?.state === 'suspended') {
    await audioState.audioContext.resume();
  }

  // Ensure a visualizer is ready before starting playback (lazy initialization)
  await ensureVisualizerReady();

  // Create new source node (they can only be played once)
  audioState.sourceNode = audioState.audioContext.createBufferSource();
  audioState.sourceNode.buffer = audioState.audioBuffer;

  // Create analyser
  audioState.analyserNode = audioState.audioContext.createAnalyser();
  audioState.analyserNode.fftSize = 2048;
  audioState.analyserNode.smoothingTimeConstant = 0;

  // Connect nodes
  audioState.sourceNode.connect(audioState.analyserNode);
  audioState.analyserNode.connect(audioState.audioContext.destination);

  // Create feature extractor if needed (lazy initialization)
  if (!audioState.featureExtractor) {
    audioState.featureExtractor = createFeatureExtractor({
      sampleRate: audioState.audioContext.sampleRate,
      fftSize: audioState.analyserNode.fftSize
    });
  }

  // Start playback
  playbackState.startTime = audioState.audioContext.currentTime - offset;
  audioState.sourceNode.start(0, offset);
  playbackState.isPlaying = true;
  updatePlayPauseButton(true);

  // Track playback started (only on new track start, not resume)
  if (offset === 0) {
    const currentTrack = playlistState.playlist[playlistState.currentTrackIndex];
    if (currentTrack) {
      trackPlaybackStarted(currentTrack.duration || 0, !!currentTrack.lyrics);
    }
  }

  // Handle end of playback - auto-advance to next track
  audioState.sourceNode.onended = async () => {
    if (playbackState.isPlaying) {
      // Find next included track
      const includedTracks = getIncludedTracks();
      const currentIncludedIdx = includedTracks.findIndex(t => t.originalIndex === playlistState.currentTrackIndex);

      if (currentIncludedIdx >= 0 && currentIncludedIdx < includedTracks.length - 1) {
        // Advance to next track
        const nextTrack = includedTracks[currentIncludedIdx + 1];
        playbackState.isPlaying = false; // Prevent recursive onended
        const loaded = await loadTrack(nextTrack.originalIndex, { seamless: true });
        if (loaded) {
          startPlayback(0);
        }
      } else {
        // End of playlist
        stopPlayback();
      }
    }
  };

  // Start animation
  animate();
}

/**
 * Pause audio playback
 */
export function pausePlayback() {
  if (!playbackState.isPlaying) return;

  playbackState.pauseTime = audioState.audioContext.currentTime - playbackState.startTime;
  playbackState.isPlaying = false;
  updatePlayPauseButton(false);
  if (audioState.sourceNode) {
    audioState.sourceNode.onended = null;
    audioState.sourceNode.stop();
    audioState.sourceNode.disconnect();
    audioState.sourceNode = null;
  }
  if (audioState.analyserNode) {
    audioState.analyserNode.disconnect();
    audioState.analyserNode = null;
  }

  if (playbackState.animationId) {
    cancelAnimationFrame(playbackState.animationId);
    playbackState.animationId = null;
  }
}

/**
 * Stop audio playback completely
 */
export function stopPlayback() {
  playbackState.isPlaying = false;
  updatePlayPauseButton(false);
  if (audioState.sourceNode) {
    try {
      audioState.sourceNode.onended = null;
      audioState.sourceNode.stop();
      audioState.sourceNode.disconnect();
    } catch (e) {}
    audioState.sourceNode = null;
  }
  if (audioState.analyserNode) {
    try {
      audioState.analyserNode.disconnect();
    } catch (e) {}
    audioState.analyserNode = null;
  }
  playbackState.pauseTime = 0;

  if (playbackState.animationId) {
    cancelAnimationFrame(playbackState.animationId);
    playbackState.animationId = null;
  }
}

/**
 * Animation loop for visualization
 */
export function animate() {
  if (!playbackState.isPlaying || !audioState.analyserNode) return;

  const currentTime = audioState.audioContext.currentTime - playbackState.startTime;
  if (elements.seekBar) elements.seekBar.value = currentTime;
  if (elements.currentTimeEl) elements.currentTimeEl.textContent = formatTime(currentTime);

  // Get frequency data (reuse buffer to avoid per-frame allocations)
  if (!audioState.frequencyDataBuffer || audioState.frequencyDataBuffer.length !== audioState.analyserNode.frequencyBinCount) {
    audioState.frequencyDataBuffer = new Uint8Array(audioState.analyserNode.frequencyBinCount);
  }
  audioState.analyserNode.getByteFrequencyData(audioState.frequencyDataBuffer);

  // Get time-domain data for CQT support (reuse buffer)
  if (!audioState.timeDomainBuffer || audioState.timeDomainBuffer.length !== audioState.analyserNode.fftSize) {
    audioState.timeDomainBuffer = new Float32Array(audioState.analyserNode.fftSize);
  }
  audioState.analyserNode.getFloatTimeDomainData(audioState.timeDomainBuffer);

  // Extract enhanced audio features (lazy evaluation - only computed if visualizer accesses them)
  const features = audioState.featureExtractor
    ? audioState.featureExtractor.extract(audioState.frequencyDataBuffer, audioState.timeDomainBuffer)
    : null;

  // Draw visualization with optional features (wrapped in try-catch for error resilience)
  try {
    managers.visualizerManager.draw(audioState.frequencyDataBuffer, features);
  } catch (error) {
    console.error('[Playback] Visualizer draw error:', error);
    // Continue animation loop despite draw errors
  }

  // Render lyrics overlay (uses separate overlay canvas - works for all visualizer types)
  if (elements.lyricsOverlayCtx && elements.lyricsOverlayCanvas) {
    elements.lyricsOverlayCtx.clearRect(0, 0, elements.lyricsOverlayCanvas.width, elements.lyricsOverlayCanvas.height);
    if (lyricsState.lyricsEnabled && lyricsState.currentLyricsScrollParams) {
      managers.lyricsRenderer.render(elements.lyricsOverlayCtx, elements.lyricsOverlayCanvas, lyricsState.currentLyricsScrollParams, currentTime);
    }
  }

  // Record frame for performance monitoring
  managers.perfMonitor.recordFrame();

  playbackState.animationId = requestAnimationFrame(animate);
}

/**
 * Set up playback control event listeners
 */
export function setupPlaybackControls() {
  // Play/Pause button
  if (elements.playPauseBtn) {
    elements.playPauseBtn.addEventListener('click', async () => {
      // Block during export
      if (isPlaybackBlocked()) return;

      // If in live mode, exit live mode and start playback
      if (uiState.isLiveMode) {
        // Import exitLiveMode dynamically to avoid circular dependency
        const { exitLiveMode } = await import('./live-mode.js');
        exitLiveMode();
        // Start playback if there's a track loaded
        if (audioState.audioBuffer) {
          startPlayback();
        }
        return;
      }

      if (playbackState.isPlaying) {
        pausePlayback();
      } else {
        // If no track is loaded yet, load the current track first
        if (!audioState.audioBuffer && playlistState.playlist.length > 0) {
          const loaded = await loadTrack(playlistState.currentTrackIndex);
          if (loaded) {
            startPlayback(0);
          }
        } else {
          startPlayback();
        }
      }
    });
  }

  // Seek bar
  if (elements.seekBar) {
    elements.seekBar.addEventListener('input', () => {
      // Block during export
      if (isPlaybackBlocked()) return;

      const wasPlaying = playbackState.isPlaying;
      if (playbackState.isPlaying) {
        pausePlayback();
      }
      playbackState.pauseTime = parseFloat(elements.seekBar.value);
      if (elements.currentTimeEl) elements.currentTimeEl.textContent = formatTime(playbackState.pauseTime);

      if (wasPlaying) {
        startPlayback(playbackState.pauseTime);
      }
    });
  }

  // Prev track button
  if (elements.prevTrackBtn) {
    elements.prevTrackBtn.addEventListener('click', async () => {
      // Block during export
      if (isPlaybackBlocked()) return;

      // In live mode, just select previous track without starting playback
      if (uiState.isLiveMode) {
        const includedTracks = getIncludedTracks();
        const currentIncludedIdx = includedTracks.findIndex(t => t.originalIndex === playlistState.currentTrackIndex);
        if (currentIncludedIdx > 0) {
          const prevTrack = includedTracks[currentIncludedIdx - 1];
          await selectTrackWithoutPlayback(prevTrack.originalIndex);
        }
        return;
      }

      // Get current playback position
      const currentTime = playbackState.isPlaying
        ? audioState.audioContext.currentTime - playbackState.startTime
        : playbackState.pauseTime;

      // If more than 3 seconds into the song, restart current track
      if (currentTime > 3) {
        const wasPlaying = playbackState.isPlaying;
        stopPlayback();
        playbackState.pauseTime = 0;
        if (elements.seekBar) elements.seekBar.value = 0;
        if (elements.currentTimeEl) elements.currentTimeEl.textContent = '0:00';
        if (wasPlaying) {
          startPlayback(0);
        }
        return;
      }

      // Otherwise go to previous track
      const includedTracks = getIncludedTracks();
      const currentIncludedIdx = includedTracks.findIndex(t => t.originalIndex === playlistState.currentTrackIndex);
      if (currentIncludedIdx > 0) {
        const prevTrack = includedTracks[currentIncludedIdx - 1];
        const loaded = await loadTrack(prevTrack.originalIndex);
        if (loaded) {
          startPlayback(0);
        }
      } else {
        // At first track, just restart
        const wasPlaying = playbackState.isPlaying;
        stopPlayback();
        playbackState.pauseTime = 0;
        if (elements.seekBar) elements.seekBar.value = 0;
        if (elements.currentTimeEl) elements.currentTimeEl.textContent = '0:00';
        if (wasPlaying) {
          startPlayback(0);
        }
      }
    });
  }

  // Next track button
  if (elements.nextTrackBtn) {
    elements.nextTrackBtn.addEventListener('click', async () => {
      // Block during export
      if (isPlaybackBlocked()) return;

      // In live mode, just select next track without starting playback
      if (uiState.isLiveMode) {
        const includedTracks = getIncludedTracks();
        const currentIncludedIdx = includedTracks.findIndex(t => t.originalIndex === playlistState.currentTrackIndex);
        if (currentIncludedIdx < includedTracks.length - 1) {
          const nextTrack = includedTracks[currentIncludedIdx + 1];
          await selectTrackWithoutPlayback(nextTrack.originalIndex);
        }
        return;
      }

      const includedTracks = getIncludedTracks();
      const currentIncludedIdx = includedTracks.findIndex(t => t.originalIndex === playlistState.currentTrackIndex);
      if (currentIncludedIdx < includedTracks.length - 1) {
        const nextTrack = includedTracks[currentIncludedIdx + 1];
        const loaded = await loadTrack(nextTrack.originalIndex);
        if (loaded) {
          startPlayback(0);
        }
      }
    });
  }
}

/**
 * Select a track for playback without starting it (used in live mode)
 * Decodes audio and updates UI but doesn't play
 */
async function selectTrackWithoutPlayback(index) {
  // Block during export
  if (isPlaybackBlocked()) return false;
  if (index < 0 || index >= playlistState.playlist.length) return false;
  if (playbackState.isLoadingTrack) return false;

  playbackState.isLoadingTrack = true;
  playbackState.loadSequence++;
  const thisLoadSequence = playbackState.loadSequence;

  try {
    playlistState.currentTrackIndex = index;
    const track = playlistState.playlist[index];

    // Create audio context if needed
    if (!audioState.audioContext) {
      audioState.audioContext = new AudioContext();
    } else if (audioState.audioContext.state === 'suspended') {
      await audioState.audioContext.resume();
    }

    // Check if a newer load was started during await
    if (playbackState.loadSequence !== thisLoadSequence) {
      return false;
    }

    // Decode audio if not already decoded
    if (!track.audioBuffer) {
      let arrayBuffer;
      if (track.isStreamed && track.streamUrl) {
        // Fetch audio from stream URL (e.g., Suno generated songs)
        const response = await fetch(track.streamUrl);
        if (!response.ok) throw new Error(`Failed to fetch stream: ${response.status}`);
        arrayBuffer = await response.arrayBuffer();
      } else if (track.file) {
        // Standard file-based track
        arrayBuffer = await track.file.arrayBuffer();
      } else {
        throw new Error('Track has no audio source (missing file and streamUrl)');
      }
      track.audioBuffer = await audioState.audioContext.decodeAudioData(arrayBuffer);
      track.duration = track.audioBuffer.duration;
    }

    // Check if a newer load was started during await
    if (playbackState.loadSequence !== thisLoadSequence) {
      return false;
    }

    audioState.audioBuffer = track.audioBuffer;

    // Update UI
    if (elements.fileNameEl) elements.fileNameEl.textContent = track.name;
    if (elements.durationEl) elements.durationEl.textContent = formatTime(track.duration);
    if (elements.seekBar) {
      elements.seekBar.max = track.duration;
      elements.seekBar.value = 0;
    }
    if (elements.currentTimeEl) elements.currentTimeEl.textContent = '0:00';
    playbackState.pauseTime = 0;

    // Update playlist UI
    const { updatePlaylistUI } = await import('./playlist.js');
    updatePlaylistUI();

    return true;

  } catch (error) {
    console.error('Error selecting track:', error);
    return false;
  } finally {
    playbackState.isLoadingTrack = false;
  }
}
